import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Crest } from "@/components/Crest";
import { PHONE_TEL, WA_DEFAULT } from "@/data/content";

const LINKS = [
  { href: "#rooms", label: "Rooms" },
  { href: "#explore", label: "Explore" },
  { href: "#amenities", label: "Amenities" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-gold/25 bg-forest-dark/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
        <a href="#top" data-testid="navbar-brand-logo" className="group flex items-center gap-3">
          <Crest className="h-11 w-11 transition-transform duration-500 group-hover:scale-110" />
          <span className="leading-tight">
            <span className="block font-display text-lg font-semibold tracking-wide text-amber-50">
              ANIL PALACE
            </span>
            <span className="block font-kannada text-[11px] tracking-wide text-gold-light">
              ಅನಿಲ್ ಪ್ಯಾಲೇಸ್ · ಹೆಮ್ಮಾಡಿ
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-100/80 transition-colors duration-300 hover:text-gold-light"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={PHONE_TEL}
            data-testid="nav-call-button"
            className="hidden items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-gold-light transition-all duration-300 hover:bg-gold hover:text-forest-dark sm:flex"
          >
            <Phone className="h-3.5 w-3.5" />
            Call
          </a>
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-button"
            className="flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-xs font-bold uppercase tracking-widest text-forest-dark transition-transform duration-300 hover:scale-105"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Book
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-menu-toggle"
            aria-label="Toggle menu"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 text-gold-light lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-gold/20 bg-forest-dark/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="border-b border-gold/10 py-3 text-sm font-semibold uppercase tracking-[0.22em] text-amber-100/90"
                >
                  {l.label}
                </a>
              ))}
              <a href={PHONE_TEL} className="py-3 text-sm font-semibold uppercase tracking-[0.22em] text-gold-light">
                Call +91 96118 29277
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
